import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import locationApi from 'api/locationApi';

import Card from 'components/App/Card';
import AppLoading from 'components/App/Loading';
import MartketDemandChart from 'components/Charts/MarketDemandChart';
import Selection from 'components/App/Selection';
import INITIAL_VALUE from 'constants/initialValue';

import styles from './MarketDemand.module.scss';

const OPTION_FILTER_BY_TIME = [
  {
    label: '365 ngày qua',
    value: 365,
  },
  {
    label: '90 ngày qua',
    value: 90,
  },
  {
    label: '30 ngày qua',
    value: 30,
  },
];

function MarketDemand(props) {
  const { placeDetail } = props;
  const provinceName = placeDetail?.province || 'TP.Hồ Chí Minh';

  const { search } = useLocation();
  const placeId = React.useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('place') || placeDetail?._id || placeDetail?.id;
  }, [search, placeDetail]);

  const [filter, setFilter] = React.useState({
    time: OPTION_FILTER_BY_TIME[0].value,
  });

  const [searchChartData, setSearchChartData] = React.useState([]);
  const [stayNightChartData, setStayNightChartData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [marketName, setMarketName] = React.useState('');
  const [marketDemandChange, setMarketDemandChange] = React.useState(0);

  const onChangeFilterByTime = React.useCallback((e) => {
    setFilter((prevState) => ({
      ...prevState,
      time: e.value,
    }));
  }, []);

  React.useEffect(() => {
    const fetchMarketDemand = async () => {
      if (!placeId) return;

      try {
        setIsLoading(true);
        const response = await locationApi.getMarketDemandCharts({
          place: placeId,
          days: filter.time,
        });

        const data = response?.payload?.data || {};
        const searchGroups = data?.searchChart?.groups || [];
        const stayNightGroups = data?.stayNightChart?.groups || [];

        const MONTH_KEYS = [
          'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
          'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
        ];

        const searchMonthGroups = searchGroups.filter((g) => MONTH_KEYS.includes(g?.key));
        const finalStayNightGroups = [...stayNightGroups];

        searchMonthGroups.forEach((monthGroup) => {
          const exists = finalStayNightGroups.some((g) => g.key === monthGroup.key);
          if (!exists) {
            finalStayNightGroups.push({
              key: monthGroup.key,
              label: monthGroup.label,
              market: 0,
              place: 0,
            });
          }
        });

        // Map groups to charts format
        const mappedSearch = searchGroups.map((group) => ({
          name: group.label,
          search: group.market,
          booking: group.place,
        }));

        const mappedStayNight = finalStayNightGroups.map((group) => ({
          name: group.label,
          search: group.market,
          booking: group.place,
        }));

        setSearchChartData(mappedSearch);
        setStayNightChartData(mappedStayNight);
        setMarketName(data?.searchChart?.market || data?.stayNightChart?.market || '');
        setMarketDemandChange(data?.marketDemandChange || 0);
      } catch (error) {
        console.error('Failed to fetch market demand charts:', error);
        setSearchChartData([]);
        setStayNightChartData([]);
        setMarketName('');
        setMarketDemandChange(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarketDemand();
  }, [placeId, filter.time]);

  const displayName = marketName || provinceName;

  return (
    <div className={styles.Components}>
      <AppLoading loading={isLoading} loaderOnPage />

      <Card className={styles.Section}>
        <div className={styles.Filter}>
          <div className={styles.FilterItem}>
            <span className={styles.Label}>
              Nhu cầu tìm kiếm trong:
            </span>

            <Selection
              onChange={onChangeFilterByTime}
              options={OPTION_FILTER_BY_TIME}
              selectedClassName={styles.Selection}
              value={filter.time}
            />
          </div>
        </div>
        <div className={styles.InfoItem}>
          <p className={styles.InfoText}>
            Nhu cầu lưu trú tại
            {' '}
            {displayName}
            {' '}
            đã
            {' '}
            {Number(marketDemandChange) >= 0
              ? 'tăng' : 'giảm'}
            {' '}
            {Math.abs(Number(marketDemandChange))}
            % trong 2 tuần qua so với 2 tuần trước đó
          </p>
        </div>

        <div>
          <div className={styles.ChartHeader}>
            <h2 className={styles.ChartName}>TÌm kiếm</h2>
            <div className={styles.LegendCustom}>
              <div className={styles.Legend}>
                <span className={`${styles.IconCircle} ${styles.LowDemand}`} />
                <span className={styles.Text}>{`Tìm kiếm ${displayName}`}</span>
              </div>
              <div className={styles.Legend}>
                <span className={`${styles.IconCircle} ${styles.AverageDemand}`} />
                <span className={styles.Text}>Đặt chỗ của tôi</span>
              </div>
            </div>
          </div>
          <div className={styles.ChartWrapper}>
            <div className={styles.ChartScroll}>
              <MartketDemandChart data={searchChartData} provinceName={displayName} />
            </div>
          </div>
        </div>
      </Card>

      <Card className={styles.Section}>
        <div>
          <div className={styles.ChartHeader}>
            <h2 className={styles.ChartName}>Thời gian lưu trú</h2>
            <div className={styles.LegendCustom}>
              <div className={styles.Legend}>
                <span className={`${styles.IconCircle} ${styles.LowDemand}`} />
                <span className={styles.Text}>{`Tìm kiếm ${displayName}`}</span>
              </div>
              <div className={styles.Legend}>
                <span className={`${styles.IconCircle} ${styles.AverageDemand}`} />
                <span className={styles.Text}>Đặt chỗ của tôi</span>
              </div>
            </div>
          </div>
          <div className={styles.ChartWrapper}>
            <div className={styles.ChartScroll}>
              <MartketDemandChart data={stayNightChartData} provinceName={displayName} />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

MarketDemand.propTypes = {
  placeDetail: PropTypes.instanceOf(Object),
};

MarketDemand.defaultProps = {
  placeDetail: INITIAL_VALUE.OBJECT,
};

export default React.memo(MarketDemand);
